import { getStyleTheme, useStyle } from "@tm/context-distribution"
import { SuggestionTextField } from "@tm/controls"
import { useLocalization } from "@tm/localization"
import { useEffect, useMemo } from "react"
import { Box, styled } from "@tm/components"
import { Controller, useForm } from "react-hook-form"
import { ZodEffects, ZodObject, ZodRawShape } from "zod"
import { RepairCoverages as GlassRepairCoverages } from "@bundles/damage-calculation-glass/components/summary/business"
import { RepairCoverages as AswegasRepairCoverages } from "@bundles/damage-calculation-aswegas/data/models"
import { zodResolver } from "@hookform/resolvers/zod"

type MainFormFields = {
    processName?: string
    repairCoverage?: AswegasRepairCoverages | GlassRepairCoverages
    hourlyRates?: boolean
}

export type RepairCoverageKey = AswegasRepairCoverages | GlassRepairCoverages

type Props = {
    onUpdateFieldValue: (path: string, value: string) => void
    onFormValid(isValid: boolean): void
    fields: Partial<MainFormFields>
    schema: ZodObject<ZodRawShape> | ZodEffects<ZodObject<ZodRawShape>>
    repairScopes?: Partial<Record<RepairCoverageKey, string>>
    onUpdateCoverageField?: (value: string) => void
}

export default function GeneralMainFields(props: Props) {
    const { fields, schema, onFormValid, onUpdateFieldValue, repairScopes, onUpdateCoverageField } = props
    const { translateText } = useLocalization()
    const style = useMemo(() => getStyle(), [])

    const {
        control,
        trigger,
        formState: { isValid },
    } = useForm<MainFormFields>({
        resolver: zodResolver(schema),
        defaultValues: fields,
        mode: "onChange",
        reValidateMode: "onBlur",
    })

    useEffect(() => {
        trigger()
    }, [])

    useEffect(() => {
        onFormValid(isValid)
    }, [isValid])

    return (
        <Box display="flex" alignItems="center">
            {"processName" in fields && (
                <Controller
                    name="processName"
                    control={control}
                    render={({ field, fieldState }) => (
                        <SuggestionTextField
                            {...field}
                            floatingLabel
                            className={style.processName}
                            label={translateText(1471)}
                            path={[field.name]}
                            onChange={(val) => {
                                field.onChange(val)
                                onUpdateFieldValue(field.name, val)
                            }}
                            fieldState={fieldState}
                        />
                    )}
                />
            )}
            {"repairCoverage" in fields && (
                <StyledBox>
                    <Controller
                        name="repairCoverage"
                        control={control}
                        render={({ field, fieldState }) => (
                            <SuggestionTextField
                                floatingLabel
                                label={translateText(3141)}
                                value={repairScopes?.[field.value as keyof typeof repairScopes] ?? ""}
                                getSuggestionData={() => Object.values(repairScopes ?? {})}
                                path={[field.name]}
                                onChangeConfirm={(val) => {
                                    const key = Object.keys(repairScopes ?? {}).find(
                                        (key) => repairScopes?.[key as keyof typeof repairScopes] === val
                                    )
                                    field.onChange(key ?? "")
                                    onUpdateCoverageField?.(key ?? "")
                                }}
                                fieldState={fieldState}
                            />
                        )}
                    />
                </StyledBox>
            )}
        </Box>
    )
}

function getStyle() {
    const theme = getStyleTheme()

    return useStyle({
        processName: {
            maxWidth: "20em",
            margin: `${theme.margin.s} ${theme.margin.s} ${theme.margin.s} 0`,
        },
        dropdown: {
            width: "15em",
            marginTop: theme.margin.s,
        },
    })(GeneralMainFields)
}

const StyledBox = styled(Box)(() => ({
    ".suggest__box": {
        paddingLeft: "0.25em",
        paddingRight: "0.25em",
    },
}))
